import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';

const DynamicInputField = ({ fieldConfig, control, errors, setValue, getValues, isDisabled }) => {
    const { name, label, type, required, options, placeholder, rules, isMulti, rows, isJson, onChange, selectProps } = fieldConfig;

    const validationRules = {
        ...(required ? { required: `${label || name} is required` } : {}),
        ...rules,
        validate: rules?.validate
            ? (value) => {
                if (typeof rules.validate === "function") {
                    return rules.validate(value, { getValues });
                }

                if (typeof rules.validate === "object") {
                    for (const key in rules.validate) {
                        const result = rules.validate[key](value, { getValues });
                        if (result !== true) return result;
                    }
                    return true;
                }

                return true;
            }
            : undefined
    };

    switch (type) {
        case 'text':
        case 'number':
            return (
                <div className={fieldConfig.className || ''}>
                    <label htmlFor={name} className="form-label">
                        {label}{required && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    <Controller
                        name={name}
                        control={control}
                        rules={validationRules}
                        render={({ field }) => (
                            <input
                                {...field}
                                id={name}
                                className="form-control"
                                type={type}
                                placeholder={placeholder}
                                disabled={isDisabled}
                            />
                        )}
                    />
                    <p className="error_msg">{errors?.[name]?.message}</p>
                </div>
            );

        case 'select':
            return (
                <div className={fieldConfig.className || ''}>
                    <label htmlFor={name} className="form-label">
                        {label}{required && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    <Controller
                        name={name}
                        control={control}
                        defaultValue={isMulti ? [] : ''}
                        rules={validationRules}
                        render={({ field }) => (
                            <Select
                                {...field}
                                inputId={name}
                                options={options}
                                isMulti={isMulti}
                                placeholder={placeholder}
                                value={field.value}
                                isDisabled={isDisabled}
                                onChange={(val) => {
                                    field.onChange(val);
                                    if (onChange) onChange(val, setValue, getValues);
                                }}
                                {...selectProps}
                            />
                        )}
                    />
                    <p className="error_msg">{errors?.[name]?.message}</p>
                </div>
            );

        case 'asyncSelect':
            return (
                <div className={fieldConfig.className || ''}>
                    <label htmlFor={name} className="form-label">
                        {label}{required && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    <Controller
                        name={name}
                        control={control}
                        defaultValue={isMulti ? [] : ''}
                        rules={validationRules}
                        render={({ field }) => (
                            <AsyncSelect
                                key={fieldConfig.cacheKey ?? name}
                                {...field}
                                inputId={name}
                                isMulti={isMulti}
                                placeholder={placeholder}
                                value={field.value}
                                isDisabled={isDisabled || !fieldConfig.loadOptions}
                                loadOptions={fieldConfig.loadOptions}
                                defaultOptions
                                cacheOptions
                                onChange={(val) => {
                                    field.onChange(val);
                                    if (onChange) onChange(val, setValue, getValues);
                                }}
                                {...selectProps}
                            />
                        )}
                    />
                    <p className="error_msg">{errors?.[name]?.message}</p>
                </div>
            );

        case 'radio':
            return (
                <div className={fieldConfig.className || ''}>
                    <label className="form-label">
                        {label}{required && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    <Controller
                        name={name}
                        control={control}
                        defaultValue={fieldConfig.defaultValue ?? options?.[0]?.value}
                        rules={validationRules}
                        render={({ field }) => (
                            <div className="d-flex gap-3">
                                {options.map(opt => (
                                    <div className="form-check" key={opt.value}>
                                        <input
                                            type="radio"
                                            id={`${name}-${opt.value}`}
                                            className="form-check-input"
                                            name={name}
                                            value={opt.value}
                                            checked={field.value === opt.value}
                                            disabled={isDisabled}
                                            onChange={() => field.onChange(opt.value)}
                                        />
                                        <label htmlFor={`${name}-${opt.value}`} className="form-check-label">
                                            {opt.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                    />
                    <p className="error_msg">{errors?.[name]?.message}</p>
                </div>
            );

        case 'textarea':
            return (
                <div className={fieldConfig.className || ''} style={{ position: 'relative' }}>
                    <label htmlFor={name} className="form-label d-flex align-items-center">
                        {label}{required && <span style={{ color: 'red' }}>*</span>}
                        {fieldConfig.renderAddon && fieldConfig.renderAddon()}
                    </label>
                    <Controller
                        name={name}
                        control={control}
                        rules={validationRules}
                        render={({ field }) => (
                            <textarea
                                {...field}
                                id={name}
                                className="form-control"
                                rows={rows || "3"}
                                placeholder={placeholder}
                                disabled={isDisabled}
                                onBlur={(e) => {
                                    if (isJson) {
                                        try {
                                            const pretty = JSON.stringify(JSON.parse(e.target.value), null, 2);
                                            setValue(name, pretty);
                                        } catch (err) {
                                            // Do nothing, let validation handle it
                                        }
                                    }
                                }}
                            />
                        )}
                    />
                    <p className="error_msg">{errors?.[name]?.message}</p>
                </div>
            );

        case 'file':
            return (
                <div className={fieldConfig.className || ''}>
                    <label htmlFor={name} className="form-label">
                        {label}{required && <span style={{ color: 'red' }}>*</span>}
                    </label>

                    <Controller
                        name={name}
                        control={control}
                        rules={validationRules}
                        render={({ field }) => (
                            <input
                                type="file"
                                id={name}
                                className="form-control"
                                disabled={isDisabled}
                                onChange={async (e) => {
                                    const file = e.target.files?.[0];

                                    field.onChange(file);

                                    if (onChange) {
                                        await onChange(file, setValue, getValues, e.target);
                                    }
                                }}
                            />
                        )}
                    />

                    <p className="error_msg">{errors?.[name]?.message}</p>
                </div>
            );

        case 'checkbox':
            return (
                <div className={fieldConfig.className || ''}>
                    <Controller
                        name={name}
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    id={name}
                                    className="form-check-input"
                                    checked={field.value || false}
                                    disabled={isDisabled}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                />
                                <label htmlFor={name} className="form-check-label">
                                    {label}
                                </label>
                            </div>
                        )}
                    />
                </div>
            );

        default:
            return null;
    }
};

export default DynamicInputField;